import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, ComponentLoader, Modal, ModalContent, ModalFooter, ModalHeader } from "@netapp/bxp-design-system-react";
import { apiClient } from "utils/clients";
import externals from "utils/externals";
import { getSafeModeHeaders } from "utils/app/utils";
import { identityAccess as i18nNamespace } from "../../../../../i18n/namespace";
import { ScopeTypeIcon } from "components/common";
import { getUserAccessAndSummary } from "../../resources/ResourceUtils";
import { Breadcrumbs } from "../../common/Breadcrumbs";
import RoleTag from "../../common/RoleTag";
import styles from "../../Settings.module.scss";
import gridStyles from "../add-userorgroup/memberPages.module.scss";

const rowsFromAssignments = (assignments) => {
    const rows = (assignments || []).map((row, index) => {
        const scopeNode = row.scopeDetails?.[row.scopeDetails.length - 1];
        const scopeId = scopeNode?.id || row[`${row.type}Id`];
        const roles = (row.multiRoles || []).filter((role) => role?.id && role?.name);
        const path = (row.scopes || []).filter(Boolean);
        if (!scopeId || roles.length === 0) {
            return null;
        }
        return {
            id: `${scopeId}-${index}`,
            type: scopeNode?.type || row.type,
            scopes: path.length > 0 ? path : [scopeNode?.name || scopeId],
            roles,
        };
    }).filter(Boolean);
    const organizations = [];
    const rest = [];
    rows.forEach((row) => {
        if (row.type === "organization") {
            organizations.push(row);
        } else {
            rest.push(row);
        }
    });
    return organizations.length > 0 ? [...organizations, ...rest] : rows;
};

const MemberRolesDialog = ({ user, closeTrigger }) => {
    const { t } = useTranslation(i18nNamespace);
    const selectedOrg = useSelector((state) => state.tenancy.selectedOrg);
    const allRoles = useSelector((state) => state.tenancy.roles) || [];
    const featuresByKey = useSelector((state) => state.features?.byKey);
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    useEffect(() => {
        if (!user?.id || !selectedOrg?.id) {
            setRows([]);
            setIsLoading(false);
            return undefined;
        }
        let cancelled = false;
        const rolesMap = {};
        allRoles.forEach((role) => {
            rolesMap[role.id] = role;
        });
        const loadRoles = async () => {
            try {
                const safeModeHeaders = getSafeModeHeaders(featuresByKey);
                const response = await apiClient.get(
                    `${externals.tenancyV4Backend}/ui/organizations/${selectedOrg.id}/users/${user.id}/uiroles?limit=10000`,
                    safeModeHeaders
                );
                const scopeDetails = getUserAccessAndSummary(response?.data, selectedOrg, rolesMap, user.id);
                if (!cancelled) {
                    setRows(rowsFromAssignments(scopeDetails?.scopeDetailsPageData));
                    setLoadError("");
                }
            } catch {
                if (!cancelled) {
                    setRows([]);
                    setLoadError(t("fail_get_user_details"));
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };
        loadRoles();
        return () => {
            cancelled = true;
        };
    }, [user?.id, selectedOrg, allRoles, featuresByKey, t]);

    return (
        <Modal className={styles["multi-role-modal-width"]} closeTrigger={closeTrigger}>
            <ModalHeader>{user?.name || t("view_details")}</ModalHeader>
            <ModalContent>
                <div className={gridStyles.assignmentGrid}>
                    {isLoading && (
                        <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
                            <ComponentLoader />
                        </div>
                    )}
                    {!isLoading && loadError && <div className={gridStyles.empty}>{loadError}</div>}
                    {!isLoading && !loadError && (
                        <div className={gridStyles.roleGridWrap}>
                            <table className={gridStyles.roleGrid}>
                                <thead>
                                    <tr>
                                        <th>{t("resource")}</th>
                                        <th>{t("roles")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 && (
                                        <tr>
                                            <td colSpan={2} className={gridStyles.empty}>{t("no_roles_assigned")}</td>
                                        </tr>
                                    )}
                                    {rows.map((row) => (
                                        <tr key={row.id}>
                                            <td>
                                                <div className={styles["roles-dialog-resource"]}>
                                                    <ScopeTypeIcon resourceType={row.type} className={styles["resource-grid-scope-icon"]} />
                                                    <Breadcrumbs items={row.scopes} />
                                                </div>
                                            </td>
                                            <td>
                                                <div className={gridStyles.chipRow}>
                                                    {row.roles.map((role) => (
                                                        <RoleTag key={role.id} name={role.name} description={role.description} />
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </ModalContent>
            <ModalFooter>
                <Button color="secondary" onClick={closeTrigger}>{t("close")}</Button>
            </ModalFooter>
        </Modal>
    );
};

export default MemberRolesDialog;
